import { Handle, Position, NodeResizer } from 'reactflow';
import { Cloud, Loader2, ArrowRight } from 'lucide-react';

interface SalesforceNodeProps {
    data: any;
    selected: boolean;
}

export default function SalesforceNode({ data, selected }: SalesforceNodeProps) {
    const isRunning = data?.executionStatus === 'Running';
    const objectName = data.objectName || 'Select Object';
    const operation = data.operation || 'insert';

    // Premium Color Palette for Salesforce Node
    // Salesforce Blue #00A1E0
    const borderColor = selected ? 'border-sky-500' : 'border-sky-200';
    const bgColor = selected ? 'bg-sky-50' : 'bg-white';
    const shadowClass = selected ? 'shadow-lg ring-2 ring-sky-100' : 'shadow-sm hover:shadow-md';

    return (
        <div className="relative flex flex-col items-center">
            {/* Resizer */}
            <NodeResizer
                color="#0ea5e9" // sky-500
                isVisible={selected}
                minWidth={120}
                minHeight={60}
                handleStyle={{ width: 8, height: 8, borderRadius: 2 }}
            />

            {/* Main Node Card */}
            <div
                className={`
                    relative group flex flex-col items-start justify-center p-3
                    min-w-[140px] rounded-xl border-2 transition-all duration-300 z-10 
                    ${borderColor} ${bgColor} ${shadowClass}
                `}
            >
                {/* Header Row: Icon + Label */}
                <div className="flex items-center gap-2 mb-2 w-full border-b border-sky-100 pb-2">
                    <Cloud className="h-5 w-5 text-[#00A1E0]" fill="#00A1E0" fillOpacity={0.2} />
                    <span className="text-[10px] font-bold text-sky-900 uppercase tracking-widest leading-none">
                        Salesforce
                    </span>
                    {isRunning && <Loader2 className="h-3 w-3 animate-spin text-sky-500 ml-auto" />}
                </div>

                {/* Content: Object Name */}
                <div className="flex flex-col gap-0.5 w-full">
                    <span className="text-[9px] text-gray-400 uppercase font-medium">Object</span>
                    <span className="text-xs font-semibold text-gray-800 truncate" title={objectName}>
                        {objectName}
                    </span>
                </div>

                {/* Footer: Operation Badge */}
                <div className="mt-2 flex items-center justify-between w-full">
                    <span className={`
                        text-[8px] font-bold px-1.5 py-0.5 rounded border uppercase
                        ${operation === 'insert' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                            operation === 'upsert' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                                'bg-rose-50 text-rose-600 border-rose-100'}
                    `}>
                        {operation}
                    </span>
                </div>

                {/* Input Handle */}
                <Handle
                    type="target"
                    position={Position.Left}
                    id="in"
                    className="!w-3 !h-3 !min-w-0 !min-h-0 !bg-white !border-2 !border-sky-400 flex items-center justify-center z-50 cursor-pointer -ml-4 shadow-sm"
                />
            </div>
        </div>
    );
}
